/**
 * Fully controlled search form used by SearchPage ("/search") during its
 * "search" step. Renders the free-text query textarea (10 char minimum, 400
 * max counter), an optional reference-image upload with preview/remove, a
 * MatchType picker (only shown when an image is attached), optional min/max
 * price inputs, and search/reset buttons. All state and handlers are lifted
 * to SearchPage via props; this component makes no API calls itself.
 */
import React, { useRef, useCallback } from "react";
import { MatchType, MatchTypes, bountyTypeDescription } from "@/types/bounty";

interface SearchFormProps {
    searchQuery: string;
    setSearchQuery: (value: string) => void;
    searchType: MatchType;
    setSearchType: (value: MatchType) => void;
    minPrice: string;
    setMinPrice: (value: string) => void;
    maxPrice: string;
    setMaxPrice: (value: string) => void;
    imagePreview: string | null;
    onImageSelect: (file: File) => void;
    onImageRemove: () => void;
    canSearch: boolean;
    onSearch: () => void;
    onStartOver: () => void;
}

const SearchForm: React.FC<SearchFormProps> = ({
    searchQuery,
    setSearchQuery,
    searchType,
    setSearchType,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    imagePreview,
    onImageSelect,
    onImageRemove,
    canSearch,
    onSearch,
    onStartOver,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) {
                onImageSelect(file);
            }
        },
        [onImageSelect]
    );

    const handleImageRemove = useCallback(() => {
        onImageRemove();
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, [onImageRemove]);

    return (
        <div className="w-full max-w-2xl mx-auto py-10 px-6 animate-fade-in">
            {/* Main description input */}
            <div className="mb-10">
                <textarea
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="describe what you're looking for..."
                    rows={3}
                    className="w-full bg-transparent border-none text-lg md:text-xl font-porsui
                        text-slate-900 placeholder:text-slate-400 focus:outline-none
                        resize-none leading-relaxed tracking-wide"
                />
                <div className="h-px bg-gradient-to-r from-black via-slate-900 to-transparent mt-2" />
                <div className="flex justify-between mt-3">
                    <span
                        className={`text-[11px] font-porsui tracking-widest uppercase transition-all duration-300 ${
                            searchQuery.length < 10 ? "text-slate-400" : "text-slate-200 opacity-0"
                        }`}
                    >
                        {searchQuery.length < 10 && `${10 - searchQuery.length} more`}
                    </span>
                    <span
                        className={`text-[11px] font-porsui tracking-widest transition-colors duration-300 ${
                            searchQuery.length > 350 ? "text-amber-400" : "text-slate-400"
                        }`}
                    >
                        {searchQuery.length}/400
                    </span>
                </div>
            </div>

            {/* Image upload */}
            <div className="mb-8">
                <span className="text-[11px] font-porsui tracking-[0.2em] uppercase text-black block mb-3">
                    reference image (optional)
                </span>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                />
                {imagePreview ? (
                    <div className="group relative inline-block">
                        <img
                            src={imagePreview}
                            alt="Reference"
                            className="w-16 h-16 object-cover rounded-lg"
                        />
                        <button
                            onClick={handleImageRemove}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-slate-900 text-white 
                                rounded-full text-xs font-porsui flex items-center justify-center
                                opacity-0 group-hover:opacity-100 transition-opacity duration-200
                                hover:bg-red-500"
                        >
                            ×
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-3 px-4 py-3 border border-dashed border-slate-400 rounded-lg
                            text-slate-500 hover:text-slate-700 hover:border-slate-500 hover:bg-slate-50
                            transition-all duration-200 group"
                    >
                        <svg
                            className="w-5 h-5 transition-transform duration-200 group-hover:scale-110"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        <span className="font-porsui text-sm">add image</span>
                    </button>
                )}
            </div>

            {/* Search type - only shown when image is provided */}
            {imagePreview && (
                <div className="mb-8">
                    <span className="text-[11px] font-porsui tracking-[0.2em] uppercase text-black block mb-3">
                        match type
                    </span>
                    <div className="grid grid-cols-3 gap-3">
                        {MatchTypes.map((type) => (
                            <button
                                key={type}
                                onClick={() => setSearchType(type)}
                                className={`group relative px-4 py-3 text-left transition-all duration-300 ease-out
                                    ${
                                        searchType === type
                                            ? "text-white"
                                            : "text-slate-600 hover:text-slate-800"
                                    }`}
                            >
                                {/* Background */}
                                <span
                                    className={`absolute inset-0 rounded-lg transition-all duration-300 ease-out
                                        ${
                                            searchType === type
                                                ? "bg-black"
                                                : "bg-slate-50 group-hover:bg-slate-100"
                                        }`}
                                />
                                <span className="relative z-10 block">
                                    <span className="font-porsui text-sm tracking-wider block mb-1">
                                        {type}
                                    </span>
                                    <span
                                        className={`font-porsui text-[11px] leading-relaxed block transition-colors duration-300
                                            ${searchType === type ? "text-slate-300" : "text-slate-400"}`}
                                    >
                                        {bountyTypeDescription(type, "you")}
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Price range */}
            <div className="mb-10">
                <span className="text-[11px] font-porsui tracking-[0.2em] uppercase text-black block mb-3">
                    price range (optional)
                </span>
                <div className="flex items-center gap-3 max-w-[280px]">
                    <div
                        className="flex items-center flex-1 border-b border-slate-400 pb-2 
                        focus-within:border-slate-900 transition-colors duration-200"
                    >
                        <span className="font-porsui text-lg text-slate-500 mr-2">$</span>
                        <input
                            type="number"
                            value={minPrice}
                            onChange={(e) => setMinPrice(e.target.value)}
                            placeholder="min"
                            min="0"
                            className="w-full bg-transparent font-porsui text-lg text-slate-900
                                placeholder:text-slate-400 focus:outline-none
                                [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                    </div>
                    <span className="text-slate-400 font-porsui text-lg">→</span>
                    <div
                        className="flex items-center flex-1 border-b border-slate-400 pb-2 
                        focus-within:border-slate-900 transition-colors duration-200"
                    >
                        <span className="font-porsui text-lg text-slate-500 mr-2">$</span>
                        <input
                            type="number"
                            value={maxPrice}
                            onChange={(e) => setMaxPrice(e.target.value)}
                            placeholder="max"
                            min="0"
                            className="w-full bg-transparent font-porsui text-lg text-slate-900
                                placeholder:text-slate-400 focus:outline-none
                                [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                    </div>
                </div>
            </div>

            {/* Search action */}
            <div className="flex items-center gap-6">
                <button
                    onClick={onSearch}
                    disabled={!canSearch}
                    className={`group relative overflow-hidden px-10 py-4 font-porsui text-sm tracking-[0.15em] uppercase
                        transition-all duration-500 ease-out
                        ${
                            canSearch
                                ? "text-white cursor-pointer"
                                : "text-slate-400 cursor-not-allowed"
                        }`}
                >
                    {/* Background layers */}
                    <span
                        className={`absolute inset-0 transition-all duration-500 ease-out
                            ${canSearch ? "bg-black" : "bg-slate-100"}`}
                    />
                    <span
                        className={`absolute inset-0 bg-gradient-to-r from-gray-800 to-black 
                            transition-all duration-300 ease-out
                            ${canSearch ? "translate-x-0 group-hover:translate-x-full" : "translate-x-full"}`}
                    />
                    <span className="relative z-10 flex items-center gap-3">
                        search
                        <svg
                            className={`w-4 h-4 transition-transform duration-300 
                                ${canSearch ? "group-hover:translate-x-1" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                        </svg>
                    </span>
                </button>

                <button
                    onClick={onStartOver}
                    className="font-porsui text-xs tracking-[0.15em] uppercase text-slate-500 
                        hover:text-slate-700 transition-colors duration-200"
                >
                    reset
                </button>
            </div>
        </div>
    );
};

export default SearchForm;
